import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import {
  USER_STATUS,
  type CLEARABLE_LOOKUP_FIELDS,
  type CLEARABLE_TEXT_FIELDS,
  type DateUserField,
} from '../constants/user.constants';

export type UserStatus = (typeof USER_STATUS)[number];

export const userListItemSchema = z.object({
  id: z.coerce.number(),
  guid: z.string(),
  fullName: z.string(),
  username: z.string(),
  hrEmpId: z.string(),
  mobile: z.string(),
  email: z.string().nullable(),
  status: z.enum(USER_STATUS),
  userTypeName: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  companyName: z.string().nullable(),
  parentUserName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
});

export const paginatedUsersSchema = z.object({
  data: z.array(userListItemSchema),
  meta: paginationMetaSchema,
});

const createUserBaseSchema = z.object({
  fullName: z
    .string()
    .min(1, 'Full name is required')
    .max(50, 'Full name must be at most 50 characters'),
  username: z
    .string()
    .min(1, 'Username is required')
    .max(25, 'Username must be at most 25 characters'),
  hrEmpId: z
    .string()
    .min(1, 'HR Employee ID is required')
    .max(20, 'Employee ID must be at most 20 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  mobile: z
    .string()
    .min(1, 'Mobile is required')
    .regex(/^01\d{9}$/, 'Mobile must be 11 digits and start with 01'),
  userTypeId: z.number({ error: 'User type is required' }),
  email: z.union([z.literal(''), z.email()]).optional(),
  companyId: z.number().optional(),
  departmentId: z.number().optional(),
  designationId: z.number().optional(),
  parentUserId: z.number().optional(),
  geoUnitId: z.number().optional(),
  accountNumber: z
    .union([
      z.literal(''),
      z
        .string()
        .regex(/^\d+$/, 'Account number must contain only digits')
        .max(20, 'Account number must be at most 20 digits'),
    ])
    .optional(),
  nickname: z.string().max(25, 'Nickname must be at most 25 characters').optional(),
  contactNo: z
    .union([
      z.literal(''),
      z.string().regex(/^01\d{9}$/, 'Alternate contact must be 11 digits and start with 01'),
    ])
    .optional(),
  address: z.string().max(150, 'Address must be at most 150 characters').optional(),
  activeFrom: z.string().optional(),
  activeTo: z.string().optional(),
});

const ACTIVE_PERIOD_ORDER_MESSAGE = 'Active To cannot be before Active From';

interface ActivePeriodValues {
  activeFrom?: string | undefined;
  activeTo?: string | undefined;
}

function addActivePeriodOrderIssue(values: ActivePeriodValues, validation: z.RefinementCtx) {
  if (!values.activeFrom || !values.activeTo || values.activeTo >= values.activeFrom) return;
  validation.addIssue({
    code: 'custom',
    path: ['activeTo'],
    message: ACTIVE_PERIOD_ORDER_MESSAGE,
  });
}

export const createUserSchema = createUserBaseSchema.superRefine((data, validation) => {
  if (data.geoUnitId === undefined) {
    validation.addIssue({
      code: 'custom',
      path: ['geoUnitId'],
      message: 'Thana selection is required',
    });
  }
  addActivePeriodOrderIssue(data, validation);
});

export const updateUserSchema = createUserBaseSchema
  .omit({ password: true })
  .partial()
  .extend({
    status: z.enum(USER_STATUS).optional(),
  })
  .superRefine(addActivePeriodOrderIssue);

export const resetPasswordSchema = z.object({
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
});

export const userDetailSchema = z.object({
  id: z.coerce.number(),
  guid: z.string(),
  fullName: z.string(),
  username: z.string(),
  hrEmpId: z.string(),
  mobile: z.string(),
  email: z.string().nullable(),
  status: z.enum(USER_STATUS),
  userTypeId: z.coerce.number(),
  userTypeName: z.string().nullable(),
  companyId: z.coerce.number().nullable(),
  companyName: z.string().nullable(),
  departmentId: z.coerce.number().nullable(),
  departmentName: z.string().nullable(),
  designationId: z.coerce.number().nullable(),
  designationName: z.string().nullable(),
  parentUserId: z.coerce.number().nullable(),
  parentUserName: z.string().nullable(),
  divisionId: z.coerce.number().nullable(),
  divisionName: z.string().nullable(),
  districtId: z.coerce.number().nullable(),
  districtName: z.string().nullable(),
  thanaId: z.coerce.number().nullable(),
  thanaName: z.string().nullable(),
  accountNumber: z.string().nullable(),
  nickname: z.string().nullable(),
  contactNo: z.string().nullable(),
  address: z.string().nullable(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  createdBy: z.coerce.number().nullable(),
  updatedBy: z.coerce.number().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const reportingAuthoritySchema = z.object({
  id: z.coerce.number(),
  fullName: z.string(),
  hrEmpId: z.string(),
  username: z.string(),
  mobile: z.string(),
  email: z.string().nullable(),
  division: z.string().nullable(),
  district: z.string().nullable(),
  thana: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
});

export const reportingAuthorityListSchema = z.array(reportingAuthoritySchema);

export type ReportingAuthority = z.infer<typeof reportingAuthoritySchema>;

export interface UserListSearch {
  userTypeId?: number | readonly number[] | undefined;
  search?: string | undefined;
  status?: UserStatus | undefined;
  departmentId?: number | undefined;
  designationId?: number | undefined;
  companyId?: number | undefined;
  geoUnitId?: number | undefined;
  divisionId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
}

export interface UserListParams extends UserListSearch {
  page?: number | undefined;
  limit?: number | undefined;
}

export type UserListItem = z.infer<typeof userListItemSchema>;
export type UserDetail = z.infer<typeof userDetailSchema>;
export type UserIdAndName = Pick<UserListItem, 'id' | 'fullName'>;
export type PaginatedUsers = z.infer<typeof paginatedUsersSchema>;
export type CreateUserPayload = z.infer<typeof createUserSchema>;
export type UpdateUserPayload = z.infer<typeof updateUserSchema>;

type ClearableLookupField = (typeof CLEARABLE_LOOKUP_FIELDS)[number];
type ClearableTextField = (typeof CLEARABLE_TEXT_FIELDS)[number] | DateUserField;

export type UpdateUserRequest = Omit<UpdateUserPayload, ClearableLookupField | ClearableTextField> &
  Partial<Record<ClearableLookupField, number | null>> &
  Partial<Record<ClearableTextField, string | null>>;

export type ResetPasswordPayload = z.infer<typeof resetPasswordSchema>;
